const API_BASE = process.env.REACT_APP_BACKEND_URL;

const api = {
  async getProducts(category = null, includeHidden = false) {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (includeHidden) params.append('include_hidden', 'true');
    const response = await fetch(`${API_BASE}/api/products?${params}`);
    return response.json();
  },
  async getProduct(productId) {
    const response = await fetch(`${API_BASE}/api/products/${productId}`);
    return response.json();
  },
  async getCategories() {
    const response = await fetch(`${API_BASE}/api/categories`);
    return response.json();
  },
  async getCart(deviceId) {
    const response = await fetch(`${API_BASE}/api/cart/${deviceId}`);
    return response.json();
  },
  async addToCart(params) {
    const { deviceId, productId, variantId, quantity, color, size } = params;
    const response = await fetch(`${API_BASE}/api/cart/add`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ device_id: deviceId, product_id: productId, variant_id: variantId, quantity, color, size })
    });
    return response.json();
  },
  async updateCartQuantity(deviceId, variantId, quantity) {
    const response = await fetch(`${API_BASE}/api/cart/${deviceId}/item/${variantId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ quantity })
    });
    return response.json();
  },
  async removeFromCart(deviceId, variantId) {
    const response = await fetch(`${API_BASE}/api/cart/${deviceId}/item/${variantId}`, { method: 'DELETE' });
    return response.json();
  },
  async createOrder(deviceId, items, shippingAddress, paymentMethod, total, email, appliedCredit = 0, promoCode = '') {
    const response = await fetch(`${API_BASE}/api/orders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ device_id: deviceId, items, shipping_address: shippingAddress, payment_method: paymentMethod, total, email, applied_credit: appliedCredit, promo_code: promoCode })
    });
    return response.json();
  },
  async validatePromo(code, subtotal, email) {
    const response = await fetch(`${API_BASE}/api/promo/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, subtotal, email: email || '' })
    });
    return response.json();
  },
  async getOrders(deviceId) {
    const response = await fetch(`${API_BASE}/api/orders/${deviceId}`);
    return response.json();
  },
  async createStripeCheckout(orderId, amount, metadata, originUrl) {
    const response = await fetch(`${API_BASE}/api/stripe/create-checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ order_id: orderId, amount, metadata, origin_url: originUrl })
    });
    return response.json();
  },
  async getStripeCheckoutStatus(sessionId) {
    const response = await fetch(`${API_BASE}/api/stripe/checkout-status/${sessionId}`);
    return response.json();
  },
  async createPayPalOrder(orderId, total) {
    const response = await fetch(`${API_BASE}/api/paypal/create-order`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ order_id: orderId, total })
    });
    return response.json();
  },
  async capturePayPalOrder(paypalOrderId, orderId) {
    const response = await fetch(`${API_BASE}/api/paypal/capture-order`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ paypal_order_id: paypalOrderId, order_id: orderId })
    });
    return response.json();
  },
  async getLoyalty(deviceId) {
    const response = await fetch(`${API_BASE}/api/loyalty/${deviceId}`);
    return response.json();
  },
  async getLookbookCollections() {
    const response = await fetch(`${API_BASE}/api/lookbook`);
    return response.json();
  },
  async getLookbookCollection(collectionId) {
    const response = await fetch(`${API_BASE}/api/lookbook/${collectionId}`);
    return response.json();
  },
  async verifyAdminPin(pin) {
    const response = await fetch(`${API_BASE}/api/admin/verify-pin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pin })
    });
    return response.json();
  },
  async adminGetProducts(includeHidden = true) {
    const response = await fetch(`${API_BASE}/api/admin/products?include_hidden=${includeHidden}`);
    return response.json();
  },
  async adminCreateProduct(product) {
    const response = await fetch(`${API_BASE}/api/admin/products`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(product)
    });
    return response.json();
  },
  async adminUpdateProduct(productId, product) {
    const response = await fetch(`${API_BASE}/api/admin/products/${productId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(product)
    });
    return response.json();
  },
  async adminUploadProductImages(productId, images) {
    const response = await fetch(`${API_BASE}/api/admin/products/${productId}/upload-images`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ images })
    });
    return response.json();
  },
  async adminDeleteProduct(productId) {
    const response = await fetch(`${API_BASE}/api/admin/products/${productId}`, { method: 'DELETE' });
    return response.json();
  },
  async adminToggleProductVisibility(productId) {
    const response = await fetch(`${API_BASE}/api/admin/products/${productId}/toggle-visibility`, { method: 'PATCH' });
    return response.json();
  },
  async adminGetOrders() {
    const response = await fetch(`${API_BASE}/api/admin/orders`);
    return response.json();
  },
  async adminUpdateOrderStatus(orderId, status) {
    const response = await fetch(`${API_BASE}/api/admin/orders/${orderId}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    });
    return response.json();
  },
  async adminDeleteOrder(orderId) {
    const response = await fetch(`${API_BASE}/api/admin/orders/${orderId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' }
    });
    return response.json();
  },
  async adminGetRevenue() {
    const response = await fetch(`${API_BASE}/api/admin/revenue`);
    return response.json();
  },
  async adminGetCustomers() {
    const response = await fetch(`${API_BASE}/api/admin/customers`);
    return response.json();
  },
  async adminGetUserProfiles() {
    const response = await fetch(`${API_BASE}/api/admin/customers`);
    return response.json();
  },
  async adminGetCustomerDetails(email) {
    const response = await fetch(`${API_BASE}/api/admin/customers/${encodeURIComponent(email)}`);
    return response.json();
  },
  
  // SIM Bookings
  async adminGetSimBookings() {
    const response = await fetch(`${API_BASE}/api/admin/sim/bookings`);
    return response.json();
  },
  async adminCreateSimBooking(booking) {
    const response = await fetch(`${API_BASE}/api/admin/sim/bookings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(booking)
    });
    return response.json();
  },
  async adminUpdateSimBooking(bookingId, booking) {
    const response = await fetch(`${API_BASE}/api/admin/sim/bookings/${bookingId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(booking)
    });
    return response.json();
  },
  async adminDeleteSimBooking(bookingId) {
    const response = await fetch(`${API_BASE}/api/admin/sim/bookings/${bookingId}`, { method: 'DELETE' });
    return response.json();
  },
  async adminChargeSimBooking(bookingId, charge) {
    const response = await fetch(`${API_BASE}/api/admin/sim/bookings/${bookingId}/charge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(charge)
    });
    return response.json();
  },
  async adminGetSimCharges() {
    const response = await fetch(`${API_BASE}/api/admin/sim/charges`);
    return response.json();
  },
  
  // User Authentication
  async userLogin(fullName, email) {
    const response = await fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ full_name: fullName, email })
    });
    return response.json();
  },
  async getUserProfile(email) {
    const response = await fetch(`${API_BASE}/api/user/profile/${encodeURIComponent(email)}`);
    return response.json();
  },
  async addToWishlist(email, productId) {
    const response = await fetch(`${API_BASE}/api/user/wishlist/${encodeURIComponent(email)}/add/${productId}`, {
      method: 'POST'
    });
    return response.json();
  },
  async removeFromWishlist(email, productId) {
    const response = await fetch(`${API_BASE}/api/user/wishlist/${encodeURIComponent(email)}/remove/${productId}`, {
      method: 'DELETE'
    });
    return response.json();
  },
  async getUserWishlist(email) {
    const response = await fetch(`${API_BASE}/api/user/wishlist/${encodeURIComponent(email)}`);
    return response.json();
  },
  
  // Newsletter
  async newsletterSubscribe(fullName, email) {
    const response = await fetch(`${API_BASE}/api/newsletter/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ full_name: fullName, email })
    });
    return response.json();
  },
  async adminGetNewsletterSubscribers() {
    const response = await fetch(`${API_BASE}/api/admin/newsletter`);
    return response.json();
  },
  async adminDeleteNewsletterSubscriber(subscriberId) {
    const response = await fetch(`${API_BASE}/api/admin/newsletter/${subscriberId}`, {
      method: 'DELETE'
    });
    return response.json();
  },
  
  // Giveaway APIs
  async enterGiveaway(name, email) {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('email', email);
    const response = await fetch(`${API_BASE}/api/giveaway/enter`, {
      method: 'POST',
      body: formData
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to enter giveaway');
    }
    return response.json();
  },
  async getGiveawayStatus() {
    const response = await fetch(`${API_BASE}/api/giveaway/status`);
    return response.json();
  },
  async adminGetGiveawayEntries() {
    const response = await fetch(`${API_BASE}/api/admin/giveaway/entries`);
    return response.json();
  },
  async adminUpdateGiveawayStatus(entryId, status) {
    const formData = new FormData();
    formData.append('status', status);
    const response = await fetch(`${API_BASE}/api/admin/giveaway/entries/${entryId}/status`, {
      method: 'PUT',
      body: formData
    });
    return response.json();
  },
  async adminDeleteGiveawayEntry(entryId) {
    const response = await fetch(`${API_BASE}/api/admin/giveaway/entries/${entryId}`, {
      method: 'DELETE'
    });
    return response.json();
  },
  async adminSendGiveawayEmail(subject, message, includeName = true) {
    const response = await fetch(`${API_BASE}/api/admin/giveaway/send-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        subject,
        message,
        include_name: includeName
      })
    });
    return response.json();
  },

  // Template Email Blasts
  async adminPreviewTemplateEmail(template) {
    const response = await fetch(`${API_BASE}/api/admin/email/template/preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ template })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to preview template');
    }
    return response.json();
  },
  async adminSendTemplateEmailTest(template, testEmail, subject) {
    const response = await fetch(`${API_BASE}/api/admin/email/template/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ template, test_email: testEmail, subject })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to send test email');
    }
    return response.json();
  },
  async adminSendTemplateEmailBlast(template, subject) {
    const response = await fetch(`${API_BASE}/api/admin/email/template/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ template, send_to_all: true, subject })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to send template blast');
    }
    return response.json();
  },
  async adminGetBlastStatus(blastId) {
    const response = await fetch(`${API_BASE}/api/admin/email/blast/${blastId}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch blast status');
    }
    return response.json();
  },

  // ---- Custom email blast ----
  async adminCustomEmailAudienceCount() {
    const response = await fetch(`${API_BASE}/api/admin/email/custom/audience-count`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to fetch audience count');
    }
    return response.json();
  },
  async adminPreviewCustomEmail(subject, bodyHtml) {
    const response = await fetch(`${API_BASE}/api/admin/email/custom/preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subject, body_html: bodyHtml }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to preview custom email');
    }
    return response.json();
  },
  async adminSendCustomEmailTest(subject, bodyHtml, testEmail) {
    const response = await fetch(`${API_BASE}/api/admin/email/custom/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subject, body_html: bodyHtml, test_email: testEmail }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Test send failed');
    }
    return response.json();
  },
  async adminSendCustomEmailBlast(subject, bodyHtml) {
    const response = await fetch(`${API_BASE}/api/admin/email/custom/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subject, body_html: bodyHtml, send_to_all: true }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Blast failed');
    }
    return response.json();
  },

  // ---- Shopify product sync ----
  async adminShopifySyncPreview() {
    const response = await fetch(`${API_BASE}/api/admin/shopify/sync/preview`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Shopify preview failed');
    }
    return response.json();
  },
  async adminShopifySyncApply() {
    const response = await fetch(`${API_BASE}/api/admin/shopify/sync`, { method: 'POST' });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Shopify sync failed');
    }
    return response.json();
  },

  // Player selection tracking
  async recordPlayerSelection(playerName, playerEmoji, fullName, email) {
    try {
      await fetch(`${API_BASE}/api/player-selection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          player_name: playerName,
          player_emoji: playerEmoji || '',
          full_name: fullName || null,
          email: email || null,
        })
      });
    } catch (e) {
      // Non-critical; swallow
    }
  },
  async adminGetPlayerStats() {
    const response = await fetch(`${API_BASE}/api/admin/player-stats`);
    if (!response.ok) throw new Error('Failed to load player stats');
    return response.json();
  },

  // Payment reconciliation + tax export
  async adminMarkOrderPaid(orderId) {
    const response = await fetch(`${API_BASE}/api/admin/orders/${orderId}/mark-paid`, {
      method: 'POST'
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.detail || 'Failed to mark paid');
    }
    return response.json();
  },
  async adminSyncPayments() {
    const response = await fetch(`${API_BASE}/api/admin/orders/sync-payments`, {
      method: 'POST'
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.detail || 'Failed to sync payments');
    }
    return response.json();
  },
  adminExportOrdersUrl(includePending = true) {
    const params = new URLSearchParams({ include_pending: includePending ? 'true' : 'false' });
    return `${API_BASE}/api/admin/export/orders?${params.toString()}`;
  },
  async adminBackfillMembers() {
    const response = await fetch(`${API_BASE}/api/admin/backfill-members`, { method: 'POST' });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.detail || 'Backfill failed');
    }
    return response.json();
  },
  async adminCreateRetryPaymentLink(orderId) {
    const response = await fetch(`${API_BASE}/api/admin/orders/${orderId}/payment-link`, {
      method: 'POST'
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.detail || 'Failed to create payment link');
    }
    return response.json();
  },
  async adminEmailRetryPaymentLink(orderId, customMessage = null) {
    const response = await fetch(`${API_BASE}/api/admin/orders/${orderId}/email-payment-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ custom_message: customMessage || null })
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.detail || 'Failed to email payment link');
    }
    return response.json();
  },
  
  // Product Image Management
  async adminRemoveProductImage(productId, imageUrl) {
    const response = await fetch(`${API_BASE}/api/admin/products/${productId}/remove-image`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image_url: imageUrl })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to remove image');
    }
    return response.json();
  },
  async adminAssignVariantImage(productId, color, imageUrl) {
    const response = await fetch(`${API_BASE}/api/admin/products/${productId}/assign-variant-image`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ color, image_url: imageUrl })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to assign image to variant');
    }
    return response.json();
  },
  async adminAssignColorSwatch(productId, color, swatchUrl) {
    const response = await fetch(`${API_BASE}/api/admin/products/${productId}/assign-color-swatch`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ color, swatch_url: swatchUrl })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to assign color swatch');
    }
    return response.json();
  },
  async adminSetMainImage(productId, imageUrl) {
    const response = await fetch(`${API_BASE}/api/admin/products/${productId}/set-main-image`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image_url: imageUrl })
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to set main image');
    }
    return response.json();
  },
  async adminTagImage(productId, imageUrl, color, angle) {
    const response = await fetch(`${API_BASE}/api/admin/products/${productId}/tag-image`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image_url: imageUrl, color, angle })
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to tag image');
    }
    return response.json();
  },
  
  // Lookbook Management
  async adminGetLookbookCollections() {
    const response = await fetch(`${API_BASE}/api/admin/lookbook`);
    return response.json();
  },
  async adminGetLookbookCollection(collectionId) {
    const response = await fetch(`${API_BASE}/api/admin/lookbook/${collectionId}`);
    return response.json();
  },
  async adminCreateLookbook(collection) {
    const response = await fetch(`${API_BASE}/api/admin/lookbook`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(collection)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create lookbook');
    }
    return response.json();
  },
  async adminUpdateLookbook(collectionId, collection) {
    const response = await fetch(`${API_BASE}/api/admin/lookbook/${collectionId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(collection)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update lookbook');
    }
    return response.json();
  },
  async adminDeleteLookbook(collectionId) {
    const response = await fetch(`${API_BASE}/api/admin/lookbook/${collectionId}`, {
      method: 'DELETE'
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete lookbook');
    }
    return response.json();
  },
  
  // Newsletter to Giveaway Sync
  async adminSyncNewsletterToGiveaway() {
    const response = await fetch(`${API_BASE}/api/admin/sync-newsletter-to-giveaway`, {
      method: 'POST'
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to sync newsletter to giveaway');
    }
    return response.json();
  },

  // ===== STORE CREDIT =====
  async adminGetCustomerCredit(email) {
    const response = await fetch(`${API_BASE}/api/admin/customers/${encodeURIComponent(email)}/credit`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to load credit');
    }
    return response.json();
  },
  async adminAdjustCustomerCredit(email, deltaDollars, reason) {
    const response = await fetch(`${API_BASE}/api/admin/customers/${encodeURIComponent(email)}/credit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ delta: deltaDollars, reason })
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to adjust credit');
    }
    return response.json();
  },
  async getProfileCredit(email) {
    const response = await fetch(`${API_BASE}/api/profile/credit?email=${encodeURIComponent(email)}`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to load credit');
    }
    return response.json();
  }
};

export default api;
